import { useNavigate } from 'react-router-dom';
import { useReveal } from '@/hooks/useReveal';
import PageLoader from '@/components/PageLoader';
import HeroCrumbs from '@/components/HeroCrumbs';
import Button from '@/components/ui/Button';
import w from './SafetyEnv.module.scss';
import { GUIDE_ICON } from '@/components/GuideIcons';

// ============================================================================
//  안전 — 온열질환 예방  (/safety/heat)
//  [2026-10-11] 기획서 v7 3-3. 7영역: ①적용현장 ②운영흐름 ③제품구성 ④단계별화면 ⑤도입유형 ⑥기록·보고 ⑦사례.
//  [2026-10-22 사용자] ★본문 화이트화 + ★★다이어그램 '페이지당 1개'(남발 금지).
//    → 이 페이지의 유일 다이어그램 = ② 셰브론(측정→기록 프로세스). 나머지 섹션은 단순 카드/텍스트.
//    ④ 위험4단계는 색 코딩이 핵심이라 '색상 카드'로 유지(다이어그램 아님). ⑦ 도입사례=시원시원한 airy 큰 카드.
//    히어로=다크 유지(내부 ghost 밝은색). 배경 위 글자=어두운색, 컬러 채운 요소 위 글자=흰색.
// ============================================================================

const HERO_IMG = '/images/hero/hero-safety.jpg';
const SLOGAN = '옥외·고온 현장의 체감온도를 실시간 측정하고, 관심·주의·경고·위험 단계에 따라 작업자 표시와 관리자 알림으로 온열질환을 예방합니다.';

const Ic = ({ n }: { n: string }) => <div className={w.iconWrap}>{GUIDE_ICON[n]}</div>;
const Reveal = ({ className = '', children }: { className?: string; children: React.ReactNode }) => {
  const ref = useReveal<HTMLDivElement>();
  return <div className={`${className} anim-scroll`} ref={ref}>{children}</div>;
};
function Soon({ label }: { label: string }) {
  return (
    <div className={w.soon} role="img" aria-label={`${label} 준비중`}>
      <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
      <span>{label} · 준비중</span>
    </div>
  );
}

export default function SafetyHeat() {
  const navigate = useNavigate();
  const nav = (to: string) => navigate(to);
  return (
    <main className={w.page}>
      <PageLoader />
      <header className={w.hero}>
        <div className={w.heroBg} style={{ backgroundImage: `url(${HERO_IMG})` }} />
        <div className={w.heroOverlay} />
        <HeroCrumbs items={['HOME', '안전', '온열질환 예방']} />
        <div className={w.heroInner}>
          <h1 className={w.heroTitle}>온열질환 예방</h1>
          <p className={w.heroSub}>{SLOGAN}</p>
          <div className={w.ctaRow} style={{ marginTop: 28 }}>
            <Button variant="pill" onClick={() => nav('/support/consult')}>현장 도입 상담</Button>
            <a className={w.ctaGhost} onClick={() => nav('/products/heat-index')}>제품(체감온도계) 보기</a>
          </div>
        </div>
      </header>

      {/* ① 적용 현장 — 단순 틴트 카드 */}
      <section className={w.section}>
        <div className={w.inner}>
          <Reveal>
            <p className={w.eyebrow}>WHERE</p>
            <h2 className={w.h2}>이런 현장에 필요합니다</h2>
            <p className={w.lead}>폭염·고온에 노출되는 옥외·다수 공정·교대 작업 현장일수록 체감온도 관리가 중요합니다.</p>
            <div className={`${w.grid} ${w.grid4}`}>
              <div className={`${w.card} ${w.cAmber} ${w.solid}`}><Ic n="building" /><h3>옥외 현장</h3><p>건설·물류 등 햇빛·복사열에 직접 노출되는 작업.</p></div>
              <div className={`${w.card} ${w.cCoral} ${w.solid}`}><Ic n="energy" /><h3>고온 공정</h3><p>제조·주물 등 공정열로 체감온도가 높은 현장.</p></div>
              <div className={`${w.card} ${w.cIndigo} ${w.solid}`}><Ic n="cycle" /><h3>다수 공정</h3><p>공정·구역마다 온도 편차가 큰 대형 사업장.</p></div>
              <div className={`${w.card} ${w.cTeal} ${w.solid}`}><Ic n="calendar" /><h3>교대 작업</h3><p>시간대별 위험이 달라지는 교대·야간 작업.</p></div>
            </div>
            <a className={w.moreLink} onClick={() => nav('/cases?tab=safety')}>안전 설치사례 보기 →</a>
          </Reveal>
        </div>
      </section>

      {/* ② 운영 흐름 — ★이 페이지의 유일 다이어그램: 셰브론 배너(5단) */}
      <section className={`${w.section} ${w.alt}`}>
        <div className={w.inner}>
          <Reveal>
            <p className={w.eyebrow}>HOW IT WORKS</p>
            <h2 className={w.h2}>측정에서 기록까지 자동으로</h2>
            <div className={`${w.chevron} ${w.chevron5}`}>
              {[
                { n: '01', t: '측정', i: 'gauge', d: '체감온도(WBGT) 실시간 측정', c: w.cBlue },
                { n: '02', t: '단계 판정', i: 'ai', d: '관심·주의·경고·위험 자동 판정', c: w.cIndigo },
                { n: '03', t: '작업자 표시', i: 'monitor', d: '현장 LCD·TV로 즉시 표시', c: w.cAmber },
                { n: '04', t: '관리자 알림', i: 'bell', d: 'SMS·카카오·앱으로 전달', c: w.cCoral },
                { n: '05', t: '기록', i: 'database', d: '측정·경보 이력 자동 저장', c: w.cTeal },
              ].map((x) => (
                <div key={x.n} className={`${w.chevronStep} ${x.c}`}>
                  <div className={w.chevronIco}>{GUIDE_ICON[x.i]}</div>
                  <span className={w.chevronNum}>{x.n}</span>
                  <h3>{x.t}</h3><p>{x.d}</p>
                </div>
              ))}
            </div>
            <a className={w.moreLink} onClick={() => nav('/safety/platform')}>통합관제 플랫폼 보기 →</a>
          </Reveal>
        </div>
      </section>

      {/* ③ 제품 구성 — 단순 틴트 카드 */}
      <section className={w.section}>
        <div className={w.inner}>
          <Reveal>
            <p className={w.eyebrow}>PRODUCTS</p>
            <h2 className={w.h2}>현장 구성</h2>
            <p className={w.lead}>체감온도계로 측정하고, 통신으로 모아, 현장 TV·중계기와 관제 대시보드로 전달합니다.</p>
            <div className={`${w.grid} ${w.grid4}`}>
              <div className={`${w.card} ${w.cCoral} ${w.solid}`}><Ic n="ai" /><h3>AI 체감온도계</h3><p>현장 WBGT를 측정하는 IoT 단말.</p></div>
              <div className={`${w.card} ${w.cBlue} ${w.solid}`}><Ic n="signal" /><h3>통신</h3><p>Wi-Fi·Ethernet·LTE로 측정값 전송.</p></div>
              <div className={`${w.card} ${w.cIndigo} ${w.solid}`}><Ic n="monitor" /><h3>TV 중계기</h3><p>현장 모니터·TV에 위험단계 표시.</p></div>
              <div className={`${w.card} ${w.cTeal} ${w.solid}`}><Ic n="gauge" /><h3>관제 대시보드</h3><p>여러 현장을 한 화면에서 관리.</p></div>
            </div>
            <div className={w.ctaRow} style={{ justifyContent: 'flex-start', marginTop: 28 }}>
              <a className={w.ctaGhost} onClick={() => nav('/products/heat-index')}>체감온도계 상세</a>
              <a className={w.ctaGhost} onClick={() => nav('/products/display-repeater')}>중계기 상세</a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ④ 위험 4단계 — 색 코딩 카드(핵심, 다이어그램 아님) */}
      <section className={`${w.section} ${w.alt}`}>
        <div className={w.inner}>
          <Reveal>
            <p className={w.eyebrow}>RISK LEVEL</p>
            <h2 className={w.h2}>위험단계별 화면·안내</h2>
            <p className={w.lead}>체감온도에 따라 관심·주의·경고·위험 4단계로 화면과 안내문구가 달라집니다. 단계가 올라갈수록 색으로 즉시 인지됩니다.</p>
            <div className={`${w.grid} ${w.grid4}`}>
              {[
                { l: '관심', i: 'monitor', d: '현황을 주기적으로 확인', c: '#2e9e6a' },
                { l: '주의', i: 'gauge', d: '수분 섭취·휴식 안내', c: '#e3b63a' },
                { l: '경고', i: 'bell', d: '휴식 강화·작업 조정', c: '#e07a2e' },
                { l: '위험', i: 'alert', d: '작업 중지 검토', c: '#d9463f' },
              ].map((x) => (
                <div key={x.l} className={`${w.card} ${w.fill}`} style={{ ['--fc' as string]: x.c } as React.CSSProperties}>
                  <Ic n={x.i} /><h3>{x.l}</h3><p>{x.d}</p>
                </div>
              ))}
            </div>
            <Soon label="단계별 UI·현장 적용사진" />
          </Reveal>
        </div>
      </section>

      {/* ⑤ 도입 유형 — feat 카드(단순) */}
      <section className={w.section}>
        <div className={w.inner}>
          <Reveal>
            <p className={w.eyebrow}>DEPLOYMENT</p>
            <h2 className={w.h2}>도입 유형</h2>
            <p className={w.lead}>현장 규모에 맞춰 단일현장부터 본사 통합관리까지 구성합니다.</p>
            <div className={`${w.grid} ${w.grid3}`}>
              <div className={`${w.card} ${w.cBlue} ${w.feat}`}><Ic n="building" /><h3>단일 현장</h3><p>한 사업장 내 측정·표시·알림을 기본 구성합니다.</p></div>
              <div className={`${w.card} ${w.cTeal} ${w.feat}`}><Ic n="cycle" /><h3>다수 현장</h3><p>여러 현장을 묶어 하나로 통합 관제합니다.</p></div>
              <div className={`${w.card} ${w.cIndigo} ${w.feat}`}><Ic n="monitor" /><h3>본사 통합관리</h3><p>본사에서 전 현장 위험현황을 관리합니다.</p></div>
            </div>
            <a className={w.moreLink} onClick={() => nav('/support/inquiry?from=/safety/heat')}>구성 견적 문의 →</a>
          </Reveal>
        </div>
      </section>

      {/* ⑥ 기록·보고 — 단순 틴트 카드 */}
      <section className={`${w.section} ${w.alt}`}>
        <div className={w.inner}>
          <Reveal>
            <p className={w.eyebrow}>RECORD</p>
            <h2 className={w.h2}>기록 · 보고</h2>
            <p className={w.lead}>측정·경보 이력과 월간 보고서로 법정기록·증빙을 지원합니다.</p>
            <div className={`${w.grid} ${w.grid3}`}>
              <div className={`${w.card} ${w.cBlue} ${w.solid}`}><Ic n="database" /><h3>측정 이력</h3><p>체감온도 측정값을 주기적으로 저장합니다.</p></div>
              <div className={`${w.card} ${w.cAmber} ${w.solid}`}><Ic n="bell" /><h3>경보 이력</h3><p>단계별 경보 발생 이력을 기록합니다.</p></div>
              <div className={`${w.card} ${w.cTeal} ${w.solid}`}><Ic n="doc" /><h3>월간 보고서</h3><p>월간 보고서를 자동 생성·활용합니다.</p></div>
            </div>
            <Soon label="보고서 예시·최신 법령·가이드" />
          </Reveal>
        </div>
      </section>

      {/* ⑦ 사례 — 시원시원한 airy 큰 카드 */}
      <section className={w.section}>
        <div className={w.inner}>
          <Reveal>
            <p className={w.eyebrow}>CASES</p>
            <h2 className={w.h2}>도입 사례</h2>
            <p className={w.lead}>건설현장·대기업·제조현장에서 온열질환 예방에 활용되고 있습니다.</p>
            <div className={`${w.grid} ${w.grid3}`}>
              <div className={`${w.card} ${w.airy} ${w.cSky} ${w.solid}`}><Ic n="building" /><h3>건설현장</h3><p>대형 건설현장 폭염 대비 도입.</p></div>
              <div className={`${w.card} ${w.airy} ${w.cIndigo} ${w.solid}`}><Ic n="solution" /><h3>대기업</h3><p>대기업 사업장 안전관리 솔루션 채택.</p></div>
              <div className={`${w.card} ${w.airy} ${w.cCoral} ${w.solid}`}><Ic n="factory" /><h3>제조현장</h3><p>고온 제조공정 체감온도 관제.</p></div>
            </div>
            <a className={w.moreLink} onClick={() => nav('/cases?tab=safety')}>안전 사례 전체 보기 →</a>
          </Reveal>
        </div>
      </section>

      {/* 핵심행동 — CTA 틴트밴드 */}
      <section className={w.finalCta}>
        <div className={w.inner}>
          <Reveal>
            <h2 className={w.h2}>현장에 맞는 온열질환 예방, 상담해 드립니다</h2>
            <p className={w.lead}>현장 조건을 알려주시면 구성과 견적을 제안합니다. 제품 바로 보기도 가능합니다.</p>
            <div className={w.ctaRow}>
              <Button variant="pill" onClick={() => nav('/support/consult')}>현장 도입 상담</Button>
              <a className={w.ctaGhost} onClick={() => nav('/support/inquiry?from=/safety/heat')}>구성 견적</a>
              <a className={w.ctaGhost} onClick={() => nav('/products/heat-index')}>제품 구매</a>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
